/**
 * A league's observed scoring baseline, and the block of it that goes into the prediction prompt.
 *
 * This exists because the model was anchoring on a generic ~2.9-goal soccer prior while MLS
 * actually runs ~3.1. **A 0.4-goal error in the expected total moves the Under 3.5 probability by
 * ~9 points** — the same magnitude as the "edge" the model was reporting — so every MLS match
 * showed a phantom Under edge, 14 of 15 of them. Feeding the real league baseline into the prompt
 * is the fix, and `promptSummary()` is that fix.
 *
 * Ported from `LeagueStats`. The prompt text is **byte-faithful** to the C# original: 168 stored
 * predictions were produced against it, and rewording it resets their comparability.
 */
import { fixed, over25, playedAt, under35, type MatchResult } from './types';

/** Top bucket of the histogram; 6 means "6 or more". */
export const MAX_TRACKED_TOTAL = 6;

/**
 * Highest over/under rung to probe when reconstructing a scoreline from settled markets. Set well
 * above the published ladder because capping truncates exactly the high-scoring matches, biasing
 * the league average DOWN — the very error this baseline exists to correct. Probing beyond the
 * published rungs costs nothing: a missing rung simply ends the count.
 */
export const MAX_LADDER_RUNG = 10;

export interface LeagueStats {
  leagueCode: string;
  displayName: string;
  matches: number;
  avgTotalGoals: number;
  avgHomeGoals: number;
  avgAwayGoals: number;
  /** Percentages, 0-100. */
  over25Rate: number;
  under35Rate: number;
  bttsRate: number;
  homeWinRate: number;
  drawRate: number;
  awayWinRate: number;
  /** Count by total goals; index MAX_TRACKED_TOTAL is "that many or more". */
  totalsDistribution: number[];
  windowStartUtc: string;
  windowEndUtc: string;
}

export function aggregate(
  leagueCode: string,
  displayName: string,
  results: readonly MatchResult[],
): LeagueStats {
  const n = results.length;
  const empty: LeagueStats = {
    leagueCode,
    displayName,
    matches: 0,
    avgTotalGoals: 0,
    avgHomeGoals: 0,
    avgAwayGoals: 0,
    over25Rate: 0,
    under35Rate: 0,
    bttsRate: 0,
    homeWinRate: 0,
    drawRate: 0,
    awayWinRate: 0,
    totalsDistribution: new Array(MAX_TRACKED_TOTAL + 1).fill(0),
    windowStartUtc: '',
    windowEndUtc: '',
  };
  if (n === 0) return empty;

  const dist = new Array<number>(MAX_TRACKED_TOTAL + 1).fill(0);
  let total = 0;
  let home = 0;
  let away = 0;
  let o25 = 0;
  let u35 = 0;
  let btts = 0;
  let homeWins = 0;
  let draws = 0;
  let awayWins = 0;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const r of results) {
    dist[Math.min(r.totalGoals, MAX_TRACKED_TOTAL)]++;
    total += r.totalGoals;
    home += r.homeGoals;
    away += r.awayGoals;
    if (over25(r)) o25++;
    if (under35(r)) u35++;
    if (r.bothTeamsScored) btts++;
    if (r.homeGoals > r.awayGoals) homeWins++;
    else if (r.homeGoals === r.awayGoals) draws++;
    else awayWins++;
    const t = playedAt(r);
    if (Number.isFinite(t)) {
      if (t < min) min = t;
      if (t > max) max = t;
    }
  }

  return {
    leagueCode,
    displayName,
    matches: n,
    avgTotalGoals: total / n,
    avgHomeGoals: home / n,
    avgAwayGoals: away / n,
    over25Rate: (100 * o25) / n,
    under35Rate: (100 * u35) / n,
    bttsRate: (100 * btts) / n,
    homeWinRate: (100 * homeWins) / n,
    drawRate: (100 * draws) / n,
    awayWinRate: (100 * awayWins) / n,
    totalsDistribution: dist,
    windowStartUtc: Number.isFinite(min) ? new Date(min).toISOString() : '',
    windowEndUtc: Number.isFinite(max) ? new Date(max).toISOString() : '',
  };
}

export const bttsNoRate = (s: LeagueStats) => 100 - s.bttsRate;

/** Roughly ±1 standard error on a rate, in percentage points — a blunt confidence cue. */
export const rateMarginPoints = (s: LeagueStats) =>
  s.matches > 0 ? 100 * Math.sqrt(0.25 / s.matches) : 0;

/** Never trust a sub-100-match league prior. 30 is the bar for showing a number without a warning. */
export const isReliable = (s: LeagueStats) => s.matches >= 30;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * `d MMM – d MMM yyyy`, matching C#'s format string.
 *
 * The C# original rendered these through `ToLocalTime()`. Here they are UTC — a **deliberate and
 * recorded** difference. It can shift a boundary label by a day on a sample that spans years, and
 * it changes nothing the model reads: the numbers, the instructions and the sample size are what
 * this block is for.
 */
export function windowDisplay(s: LeagueStats): string {
  if (s.matches === 0) return '—';
  const a = new Date(s.windowStartUtc);
  const b = new Date(s.windowEndUtc);
  return (
    `${a.getUTCDate()} ${MONTHS[a.getUTCMonth()]} – ` +
    `${b.getUTCDate()} ${MONTHS[b.getUTCMonth()]} ${b.getUTCFullYear()}`
  );
}

/** Totals histogram as a compact string, e.g. `0: 1   1: 3   2: 5 …`. */
export function distributionDisplay(s: LeagueStats): string {
  if (s.matches === 0) return '—';
  let out = '';
  for (let i = 0; i < s.totalsDistribution.length; i++) {
    const label = i === MAX_TRACKED_TOTAL ? `${i}+` : String(i);
    out += `${label}: ${s.totalsDistribution[i]}   `;
  }
  return out.replace(/\s+$/, '');
}

/**
 * The block injected into the prediction prompt.
 *
 * **Byte-faithful to `LeagueStats.PromptSummary()`.** It deliberately states the sample size so the
 * model can weigh it, and frames the numbers as a prior to start from rather than an answer. The
 * four calibration requirements are the working part: each one closes a specific way the model was
 * observed to drift.
 */
export function promptSummary(s: LeagueStats): string {
  if (s.matches === 0) return '';
  const lines: string[] = [];
  const p0 = (n: number) => fixed(n, 0);
  const p2 = (n: number) => fixed(n, 2);

  lines.push(
    `OBSERVED ${s.displayName} BASELINE (last ${s.matches} completed matches, ${windowDisplay(s)}):`,
  );
  lines.push(`- Average total goals per match: ${p2(s.avgTotalGoals)}`);
  lines.push(`- Average goals: home ${p2(s.avgHomeGoals)}, away ${p2(s.avgAwayGoals)}`);
  lines.push(`- Over 2.5 total goals hit in ${p0(s.over25Rate)}% of matches`);
  lines.push(`- Under 3.5 total goals hit in ${p0(s.under35Rate)}% of matches`);
  lines.push(`- Both teams scored in ${p0(s.bttsRate)}% of matches`);
  lines.push(
    `- Results: home win ${p0(s.homeWinRate)}%, draw ${p0(s.drawRate)}%, away win ${p0(s.awayWinRate)}%`,
  );
  lines.push(`- Distribution of total goals — ${distributionDisplay(s)}`);
  if (s.totalsDistribution[MAX_TRACKED_TOTAL] > 0) {
    lines.push(
      `  (${s.totalsDistribution[MAX_TRACKED_TOTAL]} match(es) finished with ${MAX_TRACKED_TOTAL}+ ` +
        `goals and are counted as exactly ${MAX_TRACKED_TOTAL}, so the average above is a slight ` +
        `FLOOR, not a ceiling.)`,
    );
  }
  lines.push('');
  lines.push('CALIBRATION REQUIREMENTS — these are measured facts about this league, not suggestions:');
  lines.push(
    `1. Your expected total goals should sit near ${p2(s.avgTotalGoals)} unless this specific ` +
      'fixture justifies otherwise. If your figure differs by more than 0.3, state explicitly ' +
      'in the explanation WHY this match departs from the league norm. Do not drift toward a ' +
      'generic ~2.7-2.9 soccer average — that is below this league and biases every total down.',
  );
  lines.push(
    '2. Check your own numbers for internal consistency before answering. For expected goals ' +
      'H and A, independent Poisson implies BTTS = (1-e^-H)(1-e^-A) and ' +
      'P(total<=3) = e^-T(1+T+T^2/2+T^3/6) where T = H+A. Your stated BTTS and Under 3.5 ' +
      'percentages should be close to those values — deviate only for a concrete reason ' +
      '(e.g. a side that is shut out often), and say what that reason is.',
  );
  lines.push(
    `3. Across many matches your BTTS estimates should average near ${p0(s.bttsRate)}% and your ` +
      `Under 3.5 estimates near ${p0(s.under35Rate)}%. A systematically lower estimate is a ` +
      'calibration error, not an edge.',
  );
  lines.push(
    `4. Your NUMBERS must match your OWN WORDS. If your explanation argues this is an ` +
      `above-average game for a bet, the percentage must be clearly above the league rate ` +
      `for it (${p0(s.bttsRate)}% BTTS, ${p0(s.over25Rate)}% Over 2.5, ${p0(s.under35Rate)}% Under 3.5) — ` +
      'not a point or two above, which is the same as average. If you describe compromised ' +
      'defences and functional attacks and then quote a near-baseline BTTS figure, one of the ' +
      'two is wrong. Decide which, and make them agree.',
  );
  // C#'s StringBuilder.AppendLine leaves a trailing newline on the last line.
  return lines.join('\n') + '\n';
}
